import { api } from './api.js';
import { load, save } from './storage.js';

/**
 * Offline-Outbox für Buchungen (Zeit und Material).
 *
 * Kommt eine Buchung nicht an – kein Netz, Zeitüberschreitung auf dem Handy, Sitzung abgelaufen –, wird sie hier
 * gespeichert und später automatisch erneut gesendet: beim Wiederverbinden, nach der Anmeldung, alle 30 s.
 * Jede Buchung hat einen festen Idempotency-Key; die API bucht eine Wiederholung nicht doppelt, auch wenn die
 * erste Sendung angekommen war und nur die Antwort fehlte.
 *
 * Gespeichert wird in localStorage (Buchungen sind klein; Belege bis ~1.5 MB). Einträge gehören zum Benutzer,
 * der sie erfasst hat, und werden nur mit dessen Sitzung gesendet.
 */
const KEY = 'outbox';
const MAX_BYTES = 1_500_000;
const RETRY_MS = 30_000;

let items = load(KEY, []);
const listeners = new Set();
const sentListeners = new Set();
let flushing = null;
let timer = null;

function emit() {
  save(KEY, items);
  for (const l of listeners) l();
}

/** Für useSyncExternalStore. */
export const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const snapshot = () => items;
/** Wird nach jeder erfolgreich nachgesendeten Buchung aufgerufen (z. B. um Daten neu zu laden). */
export const onSent = (fn) => { sentListeners.add(fn); return () => sentListeners.delete(fn); };

export const newKey = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);

/** Fehler, bei denen sich ein späterer Versuch lohnt (und dank Idempotency-Key gefahrlos ist). */
export const retryable = (err) => err?.status === 0 || err?.status === 401 || err?.code === 'IN_PROGRESS' || err?.status === 429;

let lastUid = null;
function schedule(ms = RETRY_MS) {
  clearTimeout(timer);
  if (items.some((i) => !i.fehler)) timer = setTimeout(() => flush(lastUid), ms);
}

/**
 * Buchung senden; wenn das nicht geht, in die Outbox legen.
 * @param {{ kind: 'leistung'|'material', nummer: string, path: string, body: any, label: string, uid: string, meta?: any }} entry
 * @returns {Promise<{ queued: false, res: any } | { queued: true, id: string }>}
 */
export async function sendOrQueue(entry) {
  const id = newKey();
  // Reihenfolge wahren: stehen schon Buchungen an, hinten anstellen und die Schlange abarbeiten.
  // Meldet das Gerät «offline», gar nicht erst senden – sofortige Rückmeldung statt Warten auf den Timeout.
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline || items.some((i) => !i.fehler && i.uid === entry.uid)) {
    if (JSON.stringify(entry.body).length > MAX_BYTES) throw new Error('Kein Netz – Buchung mit Belegen ist zu gross zum Zwischenspeichern.');
    items = [...items, { ...entry, id, erstelltAm: new Date().toISOString(), versuche: 0, fehler: null }];
    emit();
    if (!offline) flush(entry.uid); else schedule();
    return { queued: true, id };
  }
  try {
    const res = await api(entry.path, { method: 'POST', body: entry.body, idempotencyKey: id });
    return { queued: false, res };
  } catch (err) {
    if (!retryable(err)) throw err;
    if (JSON.stringify(entry.body).length > MAX_BYTES) throw err; // zu gross zum Zwischenspeichern (z. B. viele Belege)
    items = [...items, { ...entry, id, erstelltAm: new Date().toISOString(), versuche: 1, fehler: null }];
    emit();
    schedule();
    return { queued: true, id };
  }
}

/** Ausstehende Buchung zurücknehmen (z. B. «Rückgängig»), solange sie noch nicht gesendet wurde. */
export function remove(id) {
  const before = items.length;
  items = items.filter((i) => i.id !== id);
  if (items.length !== before) emit();
  return items.length !== before;
}

/**
 * Ausstehende Buchungen der Reihe nach senden. Bricht beim ersten Netzproblem ab (Reihenfolge bleibt erhalten,
 * wichtig für Material: +5 dann −5). Fachlich abgelehnte Buchungen bleiben mit Fehlermeldung stehen.
 * @param {string|null} uid  nur Einträge dieses Benutzers senden
 */
export function flush(uid = null) {
  if (flushing) return flushing;
  if (uid) lastUid = uid;
  let soon = false;
  flushing = (async () => {
    let sent = 0;
    for (const item of [...items]) {
      if (item.fehler || (uid && item.uid !== uid)) continue;
      try {
        const res = await api(item.path, { method: 'POST', body: item.body, idempotencyKey: item.id });
        items = items.filter((i) => i.id !== item.id);
        emit();
        sent++;
        for (const l of sentListeners) l(item, res);
      } catch (err) {
        if (retryable(err)) {
          soon = err.code === 'IN_PROGRESS'; // erste Sendung läuft noch → gleich nochmals nachfragen
          items = items.map((i) => (i.id === item.id ? { ...i, versuche: (i.versuche ?? 1) + 1 } : i));
          emit();
          break;
        }
        items = items.map((i) => (i.id === item.id ? { ...i, fehler: err.message } : i));
        emit();
      }
    }
    return sent;
  })().finally(() => { flushing = null; schedule(soon ? 3000 : RETRY_MS); });
  return flushing;
}

/** Noch nicht gesendete Materialmenge eines Artikels auf einem Auftrag (für die Anzeige). */
export function pendingMaterial(nummer, artikelId) {
  return items
    .filter((i) => i.kind === 'material' && !i.fehler && i.nummer === nummer)
    .reduce((s, i) => s + (Array.isArray(i.body.positionen)
      // Sammelbuchung (Auswahl gespeichert, aber noch nicht gesendet)
      ? i.body.positionen.filter((p) => p.artikelId === artikelId).reduce((t, p) => t + Number(p.menge || 0), 0)
      : i.body.artikelId === artikelId ? Number(i.body.delta || 0) : 0), 0);
}

export function _reset() { items = []; emit(); clearTimeout(timer); }
